/**
 * Online Food Ordering Protected Route
 * Route guard for authenticated and role-based access
 */

import { Box, CircularProgress } from '@mui/material';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/useAuthStore';

interface ProtectedRouteProps {
  children?: React.ReactNode;
  requiredRole?: 'admin' | 'super_admin' | 'staff';
}

export default function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const location = useLocation();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isLoading = useAuthStore((state) => state.isLoading);
  const user = useAuthStore((state) => state.user);

  // Show loading while checking auth state
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role requirements
  if (requiredRole) {
    const allowedRoles: string[] = [];

    // Super admin can access everything
    allowedRoles.push('super_admin');

    // Build allowed roles based on required role
    if (requiredRole === 'admin') {
      allowedRoles.push('admin');
    } else if (requiredRole === 'staff') {
      allowedRoles.push('admin', 'staff');
    }

    if (!user?.role || !allowedRoles.includes(user.role)) {
      // Redirect to home if insufficient permissions
      return <Navigate to="/" replace />;
    }
  }

  return children ? children : <Outlet />;
}
